/**
 * Catchball Loop Store
 * 
 * Manages Catchball Loops - the convergence mechanism inside DEGAP cycles.
 * A loop represents one complete pass of: intent → multi-level work → synthesis → return.
 * 
 * ============================================================================
 * STATE MACHINE INVARIANTS (Doc 02, Doc 03)
 * ============================================================================
 * 
 * INVARIANT 1: Catchball loops do NOT advance cycle phases.
 *   - Catchball acceptance (Accept Provisional) does NOT trigger phase transitions
 *   - Catchball is for organizational alignment, not phase control
 *   - The Cycle state machine alone governs DEGAP phases
 * 
 * INVARIANT 2: Catchball operates as a parent loop across cycles.
 *   - A single Catchball initiative can span multiple DEGAP cycles
 *   - Each cycle iteration is child work within the parent loop
 *   - Loop state (drafting → closed) is independent of cycle phase
 * 
 * INVARIANT 3: Catchball does not own Sessions.
 *   - Sessions created during Catchball belong to their Cycle
 *   - Catchball references sessions but doesn't control their state
 *   - Session state machine is independent of Catchball state machine
 * 
 * HISTORICAL NOTE: Previous implementations incorrectly allowed Catchball
 * acceptance to trigger phase advances. This was corrected per Doc 03.
 */

import { create, type StoreApi } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSafeSupabaseClient, isSupabaseAvailable } from '../../lib/supabaseSync';
import {
  type CatchballLoop,
  type CatchballLoopId,
  type CatchballLoopState,
  type CatchballLevelConfig,
  type LoopIntent,
  type LoopSynthesis,
  normalizeLoopIntent,
} from '../../types/prioritization/catchballLoop';
import { checkPolicyForCycle } from '../../lib/authorization/storeEnforcement';
import { useDegapProcessStore } from './degapProcessStore';
import { anotherRoundBlocker, isCycleOpenForLoops, CYCLE_CLOSED_MESSAGE } from '../../lib/catchball/loopGates';
import { useResponsibilityStore } from './responsibilityStore';
import { useUserStore } from './userStore';
import { useEnterpriseIdentityStore } from './enterpriseIdentityStore';
import { claimBallRpc, releaseBallRpc } from '../../lib/catchballPossessionRpc';
import type { SendTheBallRpcArgs, SendTheBallRpcResult } from '../../lib/catchballSendTheBall';

// ============================================================================
// Types
// ============================================================================

interface CatchballLoopStore {
  // State
  loops: CatchballLoop[];
  isLoading: boolean;
  error: string | null;
  /** Realtime subscription internals — one channel for the currently-watched cycle. */
  _realtimeChannel: RealtimeChannel | null;
  _realtimeCycleId: string | null;

  /**
   * Subscribe to live catchball_loops changes for a cycle (ball possession,
   * state transitions made by OTHER users). Idempotent per cycle; switching
   * cycles tears down the previous channel. Server-side RPC rejection remains
   * the correctness backstop — this only keeps the UI fresh.
   */
  subscribeToCycleLoops: (cycleId: string) => void;
  unsubscribeFromCycleLoops: () => void;


  // Actions
  /**
   * Create a new Catchball loop for a Track.
   * @param cycleId - The cycle this loop belongs to
   * @param trackId - The track this loop operates on (required per v2.3)
   * @param createdBy - User ID of the creator
   * @param levelConfig - Optional configuration for hierarchy levels
   */
  createLoop: (cycleId: string, trackId: string, createdBy: string, levelConfig?: CatchballLevelConfig) => Promise<CatchballLoop | null>;
  
  // Ball possession actions (explicit grab / release; RPCs are authoritative)
  claimBall: (loopId: CatchballLoopId, userId: string) => Promise<boolean>;
  releaseBall: (loopId: CatchballLoopId, userId: string) => Promise<boolean>;

  // Ball movement actions
  tossToMiddle: (loopId: CatchballLoopId, intent: LoopIntent, actorId: string) => Promise<boolean>;
  /**
   * "Toss the ball": intent + level-turn kickoff + toss + one sent Ball per
   * recipient in ONE transaction (RPC catchball_send_the_ball, migration 744).
   * All-or-nothing. Drive it through @/lib/catchballSendTheBall.
   */
  sendTheBall: (args: SendTheBallRpcArgs) => Promise<SendTheBallRpcResult>;
  tossToBottom: (loopId: CatchballLoopId, actorId: string) => Promise<boolean>;
  returnToMiddle: (loopId: CatchballLoopId, actorId: string) => Promise<boolean>;
  returnToTop: (loopId: CatchballLoopId, synthesis: LoopSynthesis, actorId: string) => Promise<boolean>;
  acceptOutput: (loopId: CatchballLoopId, actorId: string) => Promise<boolean>;
  requestAnotherRound: (loopId: CatchballLoopId, actorId: string) => Promise<string | null>;
  closeLoop: (loopId: CatchballLoopId, actorId: string) => Promise<boolean>;
  
  // Focus List management
  /** Links a session onto the loop. Returns false if policy/sync blocked the write. */
  /** Attach a session to the ball and include it in the next send. */
  addSessionToLoop: (loopId: CatchballLoopId, sessionId: string) => Promise<boolean>;
  /**
   * Set attachment and/or send-inclusion for one session on a ball. Omit a
   * field to leave it unchanged; `attached: false` detaches (and un-includes).
   */
  setSessionLink: (
    loopId: CatchballLoopId,
    sessionId: string,
    opts: { attached?: boolean; included?: boolean },
  ) => Promise<boolean>;
  updateLoopIntent: (loopId: CatchballLoopId, intent: Partial<LoopIntent>) => Promise<void>;
  
  /**
   * Get EGAp iterations for a loop.
   * Per parent-child model: Catchball Loops → EGAp Iterations → Sessions
   */
  getIterationsForLoop: (loopId: CatchballLoopId) => Promise<import('../../types/prioritization/egapIteration').EGApIteration[]>;
  
  // Selectors
  getLoop: (loopId: CatchballLoopId) => CatchballLoop | undefined;
  getLoopsForCycle: (cycleId: string) => CatchballLoop[];
  getCurrentLoop: (cycleId: string) => CatchballLoop | undefined;
  
  // ========================================================================
  // Proposal Packet Integration
  // ========================================================================
  
  /**
   * Get proposals for a loop using the proposalPacketStore.
   */
  getProposalsForLoop: (loopId: CatchballLoopId) => Promise<import('../../types/prioritization/catchballProposal').CatchballProposal[]>;
  
  /**
   * Check if a loop has any pending proposal packets.
   */
  hasPendingProposals: (loopId: CatchballLoopId) => Promise<boolean>;
  
  // Sync
  fetchFromSupabase: (cycleId?: string) => Promise<void>;
  
  // ========================================================================
  // Final S6 Session Result (v2.2 Canon)
  // ========================================================================
  
  /**
   * Designate a session report as the final S6 result for a loop.
   * Per Canon: "Each Track culminates in exactly one final S6 Session Result per Loop"
   * 
   * Uses the RPC: designate_final_s6_result
   */
  designateFinalS6Result: (loopId: CatchballLoopId, sessionReportId: string) => Promise<{
    success: boolean;
    error?: string;
    previousFinalId?: string;
  }>;
  
  /**
   * Get the final S6 session result for a loop.
   * Uses the RPC: get_final_s6_result
   */
  getFinalS6Result: (loopId: CatchballLoopId) => Promise<{
    found: boolean;
    sessionReportId?: string;
    sessionId?: string;
    createdAt?: Date;
    outputsSnapshot?: unknown;
  } | null>;
  
  /**
   * Check if a loop has a designated final S6 result.
   */
  hasFinalS6Result: (loopId: CatchballLoopId) => Promise<boolean>;
  
  // Utilities
  clearError: () => void;
}

// ============================================================================
// Helper: Guard process.edit for cycle
// ============================================================================

async function guardProcessEdit(cycleId: string, action: string): Promise<boolean> {
  const cycle = useDegapProcessStore.getState().getCycle(cycleId);
  if (!cycle) return false;
  const decision = await checkPolicyForCycle('process.edit', cycleId, cycle as unknown as Record<string, unknown>);
  if (!decision.allowed) {
    const reasonCode = decision.reasons[0]?.code || 'DENIED';
    console.warn('[CatchballLoopStore] Policy denied for process.edit (' + action + '):', reasonCode);
    window.dispatchEvent(new CustomEvent('authorization-denied', {
      detail: { store: 'catchballLoop', action, message: reasonCode, decision },
    }));
    return false;
  }
  return true;
}

// ============================================================================
// Helper: Map DB row (from RPC) to CatchballLoop
// ============================================================================

function rowToLoop(row: Record<string, unknown>): CatchballLoop {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    trackId: row.track_id as string,
    loopNumber: row.loop_number as number,
    state: row.state as CatchballLoopState,
    levelConfig: (row.level_config as CatchballLevelConfig) || {
      executive: [],
      middle: [],
      bottom: [],
      bottomRepresentedByLeads: true,
      defaultResponseDeadlineDays: 5,
    },
    intent: normalizeLoopIntent(row.intent),
    synthesis: row.synthesis as LoopSynthesis | undefined,
    focusListIds: (row.item_pool_ids as string[]) || [],
    sessionIds: (row.session_ids as string[]) || [],
    includedSessionIds: (row.included_session_ids as string[]) || [],
    itemPoolId: row.item_pool_id as string | undefined,
    anotherRoundRequested: (row.another_round_requested as boolean) || false,
    ballHolderScope: row.ball_holder_scope as ('executive' | 'middle' | 'bottom' | 'system') | undefined,
    ballHolderUserId: (row.ball_holder_user_id as string | null | undefined) ?? undefined,
    ballClaimedAt: (row.ball_claimed_at as string | null | undefined) ?? undefined,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
    closedAt: row.closed_at ? new Date(row.closed_at as string) : undefined,
    createdBy: (row.created_by as string | undefined) ?? '',
    orgScopeNodeId: resolveLoopOrgScopeNodeId(row),
  };
}

function resolveLoopOrgScopeNodeId(row: Record<string, unknown>): string | undefined {
  const loopScope = row.org_scope_node_id;
  if (typeof loopScope === 'string' && loopScope.length > 0) {
    return loopScope;
  }

  const cycleId = row.cycle_id;
  if (typeof cycleId !== 'string' || cycleId.length === 0) {
    return undefined;
  }

  return useDegapProcessStore.getState().getCycle(cycleId)?.orgScopeNodeId;
}

function mergeAuthoritativeLoop(
  set: StoreApi<CatchballLoopStore>['setState'],
  loop: CatchballLoop,
): void {
  set((state) => ({
    loops: state.loops.some((entry) => entry.id === loop.id)
      ? state.loops.map((entry) => (entry.id === loop.id ? loop : entry))
      : [...state.loops, loop],
    error: null,
  }));
}

async function refreshCurrentUserResponsibilities(): Promise<void> {
  const currentUserId = useUserStore.getState().userId;
  if (!currentUserId) return;
  await useResponsibilityStore.getState().refreshResponsibilities(currentUserId);
}

// ============================================================================
// Helper: refetch the watched cycle when the window regains focus
// (belt-and-braces alongside the realtime channel; registered once)
// ============================================================================

let focusRefetchRegistered = false;

function registerFocusRefetch(): void {
  if (focusRefetchRegistered || typeof window === 'undefined') return;
  focusRefetchRegistered = true;
  window.addEventListener('focus', () => {
    const { _realtimeCycleId } = useCatchballLoopStore.getState();
    if (_realtimeCycleId) {
      void useCatchballLoopStore.getState().fetchFromSupabase(_realtimeCycleId);
    }
  });
}

// ============================================================================
// Store
// ============================================================================

export const useCatchballLoopStore = create<CatchballLoopStore>()(
  (set, get) => ({
    loops: [],
    isLoading: false,
    error: null,
    _realtimeChannel: null,
    _realtimeCycleId: null,

      // ====================================================================
      // Realtime — live possession/state updates from other users
      // ====================================================================

      subscribeToCycleLoops: (cycleId) => {
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable() || !cycleId) return;

        const { _realtimeChannel, _realtimeCycleId } = get();
        if (_realtimeCycleId === cycleId && _realtimeChannel) return;
        if (_realtimeChannel) {
          void supabase.removeChannel(_realtimeChannel);
        }

        const channel = supabase
          .channel(`catchball-loops:${cycleId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'catchball_loops',
              filter: `cycle_id=eq.${cycleId}`,
            },
            (payload) => {
              if (payload.eventType === 'DELETE') {
                const oldId = (payload.old as Record<string, unknown>)?.id;
                if (typeof oldId === 'string') {
                  set((state) => ({ loops: state.loops.filter((l) => l.id !== oldId) }));
                }
                return;
              }
              mergeAuthoritativeLoop(set, rowToLoop(payload.new as Record<string, unknown>));
            }
          )
          .subscribe((status) => {
            if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
              // Degrade to refetch-on-focus (registered below); RPCs reject
              // stale actors server-side, so correctness never depends on this.
              console.warn('[CatchballLoopStore] Realtime channel degraded:', status);
            }
          });

        registerFocusRefetch();
        set({ _realtimeChannel: channel, _realtimeCycleId: cycleId });
      },

      unsubscribeFromCycleLoops: () => {
        const supabase = getSafeSupabaseClient();
        const { _realtimeChannel } = get();
        if (_realtimeChannel && supabase) {
          void supabase.removeChannel(_realtimeChannel);
        }
        set({ _realtimeChannel: null, _realtimeCycleId: null });
      },


      // ====================================================================
      // Create Loop
      // ====================================================================
      
      createLoop: async (cycleId, trackId, createdBy, levelConfig) => {
        if (!(await guardProcessEdit(cycleId, 'createLoop'))) return null;
        // Mirror of the server's cycle-open gate (763).
        const cycleForGate = useDegapProcessStore.getState().getCycle(cycleId);
        if (cycleForGate && !isCycleOpenForLoops(cycleForGate)) {
          set({ error: CYCLE_CLOSED_MESSAGE });
          return null;
        }
        if (!trackId) {
          set({ error: 'A track must be specified to create a loop' });
          return null;
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return null;
        }
        try {
          const { data, error } = await supabase.rpc('catchball_create_loop', {
            p_cycle_id: cycleId,
            p_track_id: trackId,
            p_actor_id: createdBy,
            p_level_config: levelConfig ?? undefined,
          });
          if (error) {
            set({ error: error.message });
            return null;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown> };
          if (!result?.success || !result.loop) {
            set({ error: (result as { error?: string }).error || 'Create loop failed' });
            return null;
          }
          const loop = rowToLoop(result.loop as Record<string, unknown>);
          set(state => ({
            loops: state.loops.some(l => l.id === loop.id) ? state.loops.map(l => l.id === loop.id ? loop : l) : [...state.loops, loop],
            error: null,
          }));
          await get().fetchFromSupabase(cycleId);

          // Phase B / B2: land every new loop on the ball-tree model (idempotent).
          // Root ball + initiate turn at the cycle org-scope node when known.
          // Multi-recipient fan-out is catchball_fan_out (Fan Out UI, migration 756).
          const originatorOrgUnitId =
            loop.orgScopeNodeId ??
            useDegapProcessStore.getState().getCycle(cycleId)?.orgScopeNodeId ??
            null;
          if (originatorOrgUnitId) {
            try {
              const { useCatchballTierTurnStore } = await import('./catchballTierTurnStore');
              const originatorOrgUnitName =
                useEnterpriseIdentityStore.getState().orgUnits.get(originatorOrgUnitId)?.displayName;
              await useCatchballTierTurnStore.getState().originateLoop({
                loopId: loop.id,
                originatorOrgUnitId,
                originatorOrgUnitName,
                actorId: createdBy,
              });
            } catch (originateErr) {
              console.warn('[CatchballLoopStore] originateLoop after create failed:', originateErr);
            }
          }

          return get().getLoop(loop.id) ?? loop;
        } catch (err) {
          set({ error: String(err) });
          return null;
        }
      },
      
      // ====================================================================
      // Ball Possession Actions (explicit grab / release)
      // ====================================================================

      /**
       * Claim ("grab") the unclaimed ball for a loop. The catchball_claim_ball
       * RPC is authoritative (eligibility + already-held checks happen there);
       * no client-side policy guard so any eligible tier member can grab.
       */
      claimBall: async (loopId, userId) => {
        const loop = get().getLoop(loopId);
        if (!loop) {
          set({ error: 'Loop not found' });
          return false;
        }
        if (!isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }

        try {
          const result = await claimBallRpc(loopId, userId);
          if (!result.success) {
            set({ error: result.error || 'Failed to claim the ball' });
            return false;
          }
          // The wrapper returns no loop payload — refetch the cycle so the
          // authoritative holder/claimed_at land in the store.
          await get().fetchFromSupabase(loop.cycleId);
          return true;
        } catch (err) {
          set({ error: String(err) });
          return false;
        }
      },

      /**
       * Release the ball back to "unclaimed". Holder may always release;
       * cycle-owner/admin override is enforced by the catchball_release_ball RPC.
       */
      releaseBall: async (loopId, userId) => {
        const loop = get().getLoop(loopId);
        if (!loop) {
          set({ error: 'Loop not found' });
          return false;
        }
        if (!isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }

        try {
          const result = await releaseBallRpc(loopId, userId);
          if (!result.success) {
            set({ error: result.error || 'Failed to release the ball' });
            return false;
          }
          await get().fetchFromSupabase(loop.cycleId);
          return true;
        } catch (err) {
          set({ error: String(err) });
          return false;
        }
      },

      // ====================================================================
      // Ball Movement Actions
      // ====================================================================
      // Who the ball reached is told by the database, not from here: migration
      // 850's loop trigger notifies the new tier's people (resolved from
      // level_config / the virtual config) and opens their "Form proposal"
      // tasks, whoever made the move (app, API, iOS).

      tossToMiddle: async (loopId, intent, actorId) => {
        const loop = get().getLoop(loopId);
        if (!loop) return false;
        if (!(await guardProcessEdit(loop.cycleId, 'tossToMiddle'))) return false;
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }

        try {
          if (intent) {
            const { data: intentData, error: intentError } = await supabase.rpc('catchball_update_loop_intent', {
              p_loop_id: loopId,
              p_intent_partial: intent,
              p_actor_id: actorId,
            });
            if (intentError) {
              set({ error: intentError.message });
              return false;
            }
            const intentResult = intentData as { success?: boolean; error?: string; loop?: Record<string, unknown> };
            if (!intentResult?.success) {
              set({ error: intentResult?.error || 'Failed to save loop intent before handoff' });
              return false;
            }
            if (intentResult.loop) {
              mergeAuthoritativeLoop(set, rowToLoop(intentResult.loop));
            }
          }

          const { data, error } = await supabase.rpc('catchball_toss_to_middle', {
            p_loop_id: loopId,
            p_intent: null,
            p_actor_id: actorId,
          });
          if (error) {
            set({ error: error.message });
            return false;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown> };
          if (!result?.success || !result.loop) {
            set({ error: result?.error || 'Failed to toss loop to next org level' });
            return false;
          }
          const updatedLoop = rowToLoop(result.loop);
          mergeAuthoritativeLoop(set, updatedLoop);
          // Do not block the composer confirm modal on refresh / responsibility sync.
          void get().fetchFromSupabase(loop.cycleId);
          void refreshCurrentUserResponsibilities();
          return true;
        } catch (err) {
          set({ error: String(err) });
          return false;
        }
      },

      sendTheBall: async ({ loopId, intent, target, recipients }) => {
        const loop = get().getLoop(loopId);
        if (!loop) return { success: false, error: 'Loop not found' };
        if (!(await guardProcessEdit(loop.cycleId, 'sendTheBall'))) {
          return { success: false, error: 'This cycle cannot be edited right now.' };
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          return { success: false, error: 'Supabase not available' };
        }
        const { data, error } = await supabase.rpc('catchball_send_the_ball', {
          p_loop_id: loopId,
          p_intent: intent,
          p_target: target,
          p_recipients: recipients,
        });
        if (error) return { success: false, error: error.message };
        const result = (data ?? { success: false }) as SendTheBallRpcResult;
        if (!result.success || !result.loop) return result;

        // Committed. The database told everyone the ball reached: the tier's
        // people and their "Form proposal" tasks (850 loop trigger), and each
        // Ball recipient (737 trigger on the Ball insert).
        mergeAuthoritativeLoop(set, rowToLoop(result.loop));
        void get().fetchFromSupabase(loop.cycleId);
        void refreshCurrentUserResponsibilities();
        return result;
      },

      tossToBottom: async (loopId, actorId) => {
        const loop = get().getLoop(loopId);
        if (!loop) return false;
        if (!(await guardProcessEdit(loop.cycleId, 'tossToBottom'))) return false;
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }

        try {
          const { data, error } = await supabase.rpc('catchball_toss_to_bottom', {
            p_loop_id: loopId,
            p_actor_id: actorId,
          });
          if (error) {
            set({ error: error.message });
            return false;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown> };
          if (!result?.success || !result.loop) {
            set({ error: result?.error || 'Failed to toss loop further down the org tree' });
            return false;
          }
          const updatedLoop = rowToLoop(result.loop);
          mergeAuthoritativeLoop(set, updatedLoop);
          await get().fetchFromSupabase(loop.cycleId);
          await refreshCurrentUserResponsibilities();
          return true;
        } catch (err) {
          set({ error: String(err) });
          return false;
        }
      },

      returnToMiddle: async (loopId, actorId) => {
        const loop = get().getLoop(loopId);
        if (!loop) return false;
        if (!(await guardProcessEdit(loop.cycleId, 'returnToMiddle'))) return false;
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }

        try {
          const { data, error } = await supabase.rpc('catchball_return_to_middle', {
            p_loop_id: loopId,
            p_actor_id: actorId,
          });
          if (error) {
            set({ error: error.message });
            return false;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown> };
          if (!result?.success || !result.loop) {
            set({ error: result?.error || 'Failed to return loop to the previous org level' });
            return false;
          }
          const updatedLoop = rowToLoop(result.loop);
          mergeAuthoritativeLoop(set, updatedLoop);
          await get().fetchFromSupabase(loop.cycleId);
          await refreshCurrentUserResponsibilities();
          return true;
        } catch (err) {
          set({ error: String(err) });
          return false;
        }
      },

      returnToTop: async (loopId, synthesis, actorId) => {
        const loop = get().getLoop(loopId);
        if (!loop) return false;
        if (!(await guardProcessEdit(loop.cycleId, 'returnToTop'))) return false;
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }

        try {
          const { data, error } = await supabase.rpc('catchball_return_to_top', {
            p_loop_id: loopId,
            p_synthesis: synthesis,
            p_actor_id: actorId,
          });
          if (error) {
            set({ error: error.message });
            return false;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown> };
          if (!result?.success || !result.loop) {
            set({ error: result?.error || 'Failed to return synthesis upward' });
            return false;
          }
          const authoritativeLoop = rowToLoop(result.loop);
          mergeAuthoritativeLoop(set, authoritativeLoop);
          await get().fetchFromSupabase(loop.cycleId);
          await refreshCurrentUserResponsibilities();
          return true;
        } catch (err) {
          set({ error: String(err) });
          return false;
        }
      },
      
      /**
       * Accept loop output (executive approval).
       * 
       * INVARIANT: This does NOT advance cycle phases (per Doc 03).
       * Accepting a Catchball loop output transitions the loop to 'output_ready'
       * and creates a governance event, but the Cycle remains in its current phase.
       * 
       * Phase advancement requires explicit action via degapProcessStore.transitionPhase().
       */
      acceptOutput: async (loopId, actorId) => {
        const loop = get().getLoop(loopId);
        if (!loop) return false;
        if (!(await guardProcessEdit(loop.cycleId, 'acceptOutput'))) return false;
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }

        try {
          const { data, error } = await supabase.rpc('catchball_accept_output', {
            p_loop_id: loopId,
            p_actor_id: actorId,
          });
          if (error) {
            set({ error: error.message });
            return false;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown> };
          if (!result?.success || !result.loop) {
            set({ error: result?.error || 'Failed to accept loop output' });
            return false;
          }
          const authoritativeLoop = rowToLoop(result.loop);
          mergeAuthoritativeLoop(set, authoritativeLoop);
          await get().fetchFromSupabase(loop.cycleId);
          await refreshCurrentUserResponsibilities();
          return true;
        } catch (err) {
          set({ error: String(err) });
          return false;
        }
      },
      
      requestAnotherRound: async (loopId, actorId) => {
        const loop = get().getLoop(loopId);
        if (!loop) return null;
        if (!(await guardProcessEdit(loop.cycleId, 'requestAnotherRound'))) return null;
        // Mirror of the server's another-round gates (763): open loop, open
        // cycle, holder, intent.maxIterations.
        const blocker = anotherRoundBlocker(
          loop,
          get().getLoopsForCycle(loop.cycleId),
          useDegapProcessStore.getState().getCycle(loop.cycleId),
          actorId,
        );
        if (blocker) {
          set({ error: blocker });
          return null;
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return null;
        }

        try {
          const { data, error } = await supabase.rpc('catchball_request_another_round', {
            p_loop_id: loopId,
            p_actor_id: actorId,
          });
          if (error) {
            set({ error: error.message });
            return null;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown>; new_loop_id?: string };
          if (!result?.success || !result.loop) {
            set({ error: result?.error || 'Failed to start another round' });
            return null;
          }
          mergeAuthoritativeLoop(set, rowToLoop(result.loop));
          await get().fetchFromSupabase(loop.cycleId);
          await refreshCurrentUserResponsibilities();
          return result.new_loop_id ?? (result.loop.id as string | undefined) ?? null;
        } catch (err) {
          set({ error: String(err) });
          return null;
        }
      },
      
      /**
       * Close a loop.
       * Per Window 4: Validates that loop has accepted proposal and priority_set before allowing closure.
       */
      closeLoop: async (loopId, actorId) => {
        const loop = get().getLoop(loopId);
        if (!loop) {
          set({ error: 'Loop not found' });
          return false;
        }
        if (!(await guardProcessEdit(loop.cycleId, 'closeLoop'))) return false;
        
        // Per Window 4: Loop cannot close without accepted proposal and priority_set
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }
        {
          // Check for accepted proposal
          const { data: proposals, error: proposalError } = await supabase
            .from('catchball_proposals')
            .select('id')
            .eq('loop_id', loopId)
            .eq('status', 'accepted')
            .limit(1);
          
          if (proposalError) {
            console.error('[CatchballLoopStore] Error checking proposals:', proposalError);
          } else if (!proposals || proposals.length === 0) {
            console.warn('[CatchballLoopStore] Cannot close loop: no accepted proposal');
            set({ error: 'Cannot close loop: no accepted proposal exists' });
            return false;
          }
          
          // Check for priority_set
          const { data: prioritySets, error: psError } = await supabase
            .from('priority_sets')
            .select('id')
            .eq('loop_id', loopId)
            .limit(1);
          
          if (psError) {
            console.error('[CatchballLoopStore] Error checking priority_sets:', psError);
          } else if (!prioritySets || prioritySets.length === 0) {
            console.warn('[CatchballLoopStore] Cannot close loop: no priority_set created');
            set({ error: 'Cannot close loop: priority list must be created before closing' });
            return false;
          }
        }
        
        try {
          const { data, error } = await supabase.rpc('catchball_close_loop', {
            p_loop_id: loopId,
            p_actor_id: actorId,
          });
          if (error) {
            set({ error: error.message });
            return false;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown> };
          if (!result?.success || !result.loop) {
            set({ error: result?.error || 'Failed to close loop' });
            return false;
          }
          mergeAuthoritativeLoop(set, rowToLoop(result.loop));
          await get().fetchFromSupabase(loop.cycleId);
          await refreshCurrentUserResponsibilities();
          return true;
        } catch (err) {
          set({ error: String(err) });
          return false;
        }
      },
      
      // ====================================================================
      // Focus List / Session Management
      // ====================================================================
      
      addSessionToLoop: async (loopId, sessionId) =>
        get().setSessionLink(loopId, sessionId, { attached: true, included: true }),

      /**
       * The one write path for "what is on this ball, and what goes up next".
       *
       * Was a client-side whole-row upsert with the authority check evaluated in
       * the browser. Both facts now move through catchball_set_session_link
       * (§2.3a): the same holder / session-actor / process.edit rule runs
       * server-side, attachment and inclusion cannot half-apply, and detach —
       * which had no path at all — is just `attached: false`.
       */
      setSessionLink: async (loopId, sessionId, opts) => {
        const loop = get().getLoop(loopId);
        if (!loop) {
          set({ error: 'Loop not found' });
          return false;
        }
        const actorId = useUserStore.getState().userId;
        if (!actorId) {
          set({ error: 'You must be signed in to change what goes up on this ball.' });
          return false;
        }
        const supabase = getSafeSupabaseClient();
        if (!supabase || !isSupabaseAvailable()) {
          set({ error: 'Supabase not available' });
          return false;
        }

        const { data, error } = await supabase.rpc('catchball_set_session_link', {
          p_loop_id: loopId,
          p_session_id: sessionId,
          p_actor_id: actorId,
          p_attached: opts.attached ?? null,
          p_included: opts.included ?? null,
        });
        if (error) {
          set({ error: error.message });
          return false;
        }
        const result = data as {
          success?: boolean;
          error?: string;
          session_ids?: string[];
          included_session_ids?: string[];
        } | null;
        if (!result?.success) {
          set({ error: result?.error || 'Could not change what goes up on this ball.' });
          return false;
        }

        const updated: CatchballLoop = {
          ...loop,
          sessionIds: result.session_ids ?? loop.sessionIds,
          includedSessionIds: result.included_session_ids ?? loop.includedSessionIds,
          updatedAt: new Date(),
        };
        set((state) => ({
          loops: state.loops.map((l) => (l.id === loopId ? updated : l)),
          error: null,
        }));
        return true;
      },

      updateLoopIntent: async (loopId, intent) => {
        const loop = get().getLoop(loopId);
        if (!loop) return;
        if (!(await guardProcessEdit(loop.cycleId, 'updateLoopIntent'))) return;
        const supabase = getSafeSupabaseClient();
        const actorId = useUserStore.getState().userId || loop.createdBy;
        if (!supabase || !isSupabaseAvailable() || !actorId) {
          set({ error: 'Supabase or actor not available' });
          return;
        }

        try {
          const { data, error } = await supabase.rpc('catchball_update_loop_intent', {
            p_loop_id: loopId,
            p_intent_partial: intent,
            p_actor_id: actorId,
          });
          if (error) {
            set({ error: error.message });
            return;
          }
          const result = data as { success?: boolean; error?: string; loop?: Record<string, unknown> };
          if (!result?.success || !result.loop) {
            set({ error: result?.error || 'Failed to update loop intent' });
            return;
          }
          mergeAuthoritativeLoop(set, rowToLoop(result.loop));
        } catch (err) {
          set({ error: String(err) });
        }
      },
      
      getIterationsForLoop: async (loopId) => {
        const { useEGApIterationStore } = await import('./egapIterationStore');
        return useEGApIterationStore.getState().getIterationsForLoop(loopId);
      },
      
      // ====================================================================
      // Selectors
      // ====================================================================
      
      getLoop: (loopId) => {
        return get().loops.find(l => l.id === loopId);
      },
      
      getLoopsForCycle: (cycleId) => {
        // loopNumber alone is NOT unique within a cycle: the create-loop RPC
        // numbers per track, so multi-path launches yield several "Loop 1"s.
        // Tie-break on createdAt so ordering (and getCurrentLoop's pick) is
        // deterministic instead of inheriting arbitrary store/DB row order.
        return get().loops
          .filter(l => l.cycleId === cycleId)
          .sort(
            (a, b) =>
              a.loopNumber - b.loopNumber ||
              (a.createdAt?.getTime?.() ?? 0) - (b.createdAt?.getTime?.() ?? 0),
          );
      },
      
      getCurrentLoop: (cycleId) => {
        const cycleLoops = get().getLoopsForCycle(cycleId);
        // Return the most recent non-closed loop, or the last one
        const activeLoop = cycleLoops.find(l => l.state !== 'closed');
        return activeLoop || cycleLoops[cycleLoops.length - 1];
      },
      
      // ====================================================================
      // Sync
      // ====================================================================
      
      fetchFromSupabase: async (cycleId) => {
        if (!isSupabaseAvailable()) {
          console.log('[CatchballLoopStore] Supabase not available');
          return;
        }

        // Watching a specific cycle implies wanting to stay current on it —
        // possession changes made by other users must reach this client.
        if (cycleId) {
          get().subscribeToCycleLoops(cycleId);
        }

        set({ isLoading: true, error: null });
        
        try {
          const supabase = getSafeSupabaseClient();
          if (!supabase) return;
          
          // Deterministic order matters: multi-path launches create one loop
          // per track with EQUAL loop numbers (the RPC numbers per track), so
          // consumers that sort by loopNumber tie and fall back to this array
          // order. Without ORDER BY, Postgres returns heap order, which
          // reshuffles after updates — the tie then resolves differently per
          // fetch and can pair a path's surface with the other path's loop.
          let query = supabase
            .from('catchball_loops')
            .select('*')
            .order('created_at', { ascending: true });
          
          if (cycleId) {
            query = query.eq('cycle_id', cycleId);
          }
          
          const { data, error } = await query;
          
          if (error) {
            console.error('[CatchballLoopStore] Fetch error:', error);
            set({ isLoading: false, error: error.message });
            return;
          }
          
          if (data) {
            const loops: CatchballLoop[] = data.map((row: Record<string, unknown>) => rowToLoop(row));
            
            // Merge with existing loops (preserve any not in the fetch)
            set(state => {
              const otherLoops = cycleId
                ? state.loops.filter(l => l.cycleId !== cycleId)
                : [];
              
              return {
                loops: [...otherLoops, ...loops],
                isLoading: false,
              };
            });
            
            console.log('[CatchballLoopStore] Loaded', loops.length, 'loops from Supabase');
          }
        } catch (err) {
          console.error('[CatchballLoopStore] Fetch exception:', err);
          set({ isLoading: false, error: String(err) });
        }
      },
      
      // ========================================================================
      // Proposal Packet Integration
      // ========================================================================
      
      getProposalsForLoop: async (loopId: CatchballLoopId) => {
        // Import dynamically to avoid circular dependency
        const { useProposalPacketStore } = await import('./proposalPacketStore');
        return useProposalPacketStore.getState().fetchProposalsForLoop(loopId);
      },
      
      hasPendingProposals: async (loopId: CatchballLoopId) => {
        const proposals = await get().getProposalsForLoop(loopId);
        return proposals.some(p => p.status === 'pending');
      },
      
      // ========================================================================
      // Final S6 Session Result (v2.2 Canon)
      // ========================================================================
      
      designateFinalS6Result: async (loopId: CatchballLoopId, sessionReportId: string) => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          return { success: false, error: 'Supabase not available' };
        }
        
        try {
          const { data, error } = await supabase
            .rpc('designate_final_s6_result', {
              p_session_report_id: sessionReportId,
            });
          
          if (error) {
            console.error('[CatchballLoopStore] designateFinalS6Result RPC error:', error);
            return { success: false, error: error.message };
          }
          
          if (data && typeof data === 'object') {
            const result = data as { success: boolean; error?: string; previous_final_id?: string };
            if (result.success) {
              console.log('[CatchballLoopStore] Designated final S6 result:', sessionReportId, 'for loop:', loopId);
              return {
                success: true,
                previousFinalId: result.previous_final_id,
              };
            }
            return { success: false, error: result.error || 'Unknown error' };
          }
          
          return { success: false, error: 'Unexpected response format' };
        } catch (err) {
          console.error('[CatchballLoopStore] designateFinalS6Result exception:', err);
          return { success: false, error: String(err) };
        }
      },
      
      getFinalS6Result: async (loopId: CatchballLoopId) => {
        const supabase = getSafeSupabaseClient();
        if (!supabase) {
          return null;
        }
        
        try {
          const { data, error } = await supabase
            .rpc('get_final_s6_result', {
              p_loop_id: loopId,
            });
          
          if (error) {
            console.error('[CatchballLoopStore] getFinalS6Result RPC error:', error);
            return null;
          }
          
          if (data && typeof data === 'object') {
            const result = data as {
              found: boolean;
              session_report_id?: string;
              session_id?: string;
              created_at?: string;
              outputs_snapshot?: unknown;
            };
            
            return {
              found: result.found,
              sessionReportId: result.session_report_id,
              sessionId: result.session_id,
              createdAt: result.created_at ? new Date(result.created_at) : undefined,
              outputsSnapshot: result.outputs_snapshot,
            };
          }
          
          return { found: false };
        } catch (err) {
          console.error('[CatchballLoopStore] getFinalS6Result exception:', err);
          return null;
        }
      },
      
      hasFinalS6Result: async (loopId: CatchballLoopId) => {
        const result = await get().getFinalS6Result(loopId);
        return result?.found === true;
      },
      
      clearError: () => {
        set({ error: null });
      },
    })
);

export default useCatchballLoopStore;
